import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  useDocumentContext,
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
} from '../../context/complexDocumentContext';
import ComplexEmailVariableList from './ComplexEmailVariableList';
import { BusinessLogic } from '../../context/businessLogicContext';
import { VARIABLE_OBJECT_TYPES, VARIABLE_TYPE } from '../../utils/constants/appConstants';
import { CONTEXT_KEYS, TEMPLATE_TYPE, useArtboardContext } from '../../context/complexArtboardContext';
import { COMPLEX_EMAIL_CONTEXT_KEYS, useEmailContext } from '../../context/complexEmailContext';
import { ComplexPreviewTabsContainer } from '../ComplexDocumentDesign/styles';
import { Tabs } from 'antd';
import { ComplexVariableOverrideList } from '../ComplexDocumentDesign/complexVariables/ComplexVariableOverrideList';
import { useTranslation } from 'react-i18next';
import { COMPLEX_VARIABLES_SIDEBAR } from '../ComplexDocumentDesign/complexVariables/translationValues';
import { getLocalVariable, getStoreStaticTableBlock, mergeLocalVariable } from '../ComplexDocumentDesign/complexDocumentHelper';
import { deepCopy } from '../../utils/javascript';
import { getComplexDocument } from '../../utils/services/complexDocumentsAPI';
import { STATIC_TABLE_BLOCK } from '../../utils/complexDocumentConstants';
import { useSessionContext } from '../../context/sessionContext';
import { checkUserAccessRoles } from '../../utils/helper';

const { TabPane } = Tabs;
const EMPTY_LIST = [];
const NAMESPACES = ['COMPLEX_VARIABLES_SIDEBAR'];
const PRIMITIVE_VARIABLE_TYPES = ['string', 'String', 'Date', 'date'];

const ComplexEmailVariablesTab = ({ visible, editable, setIsHtmlExpanded, isHtmlExpanded }) => {
  const { t } = useTranslation(NAMESPACES);

  const variablesSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [allVariables] = useDocumentContext(variablesSelector, 'ComplexEmailVariablesTab');

  const { businessLogics } = useContext(BusinessLogic);

  const fromAddressSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.FROM_ADDRESS], []);
  const [fromAddressId] = useEmailContext(fromAddressSelector, 'ComplexEmailVariablesTab');

  const subjectSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.EMAIL_SUBJECT], []);
  const [subject, emailMethods] = useEmailContext(subjectSelector, 'ComplexEmailVariablesTab');

  const attachmentSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS], []);
  const [attachments] = useEmailContext(attachmentSelector, 'ComplexEmailVariablesTab');

  const fetchingVariablesSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FETCHING_GLOBAL_VARIABLES], []);
  const [variablesLoading, method] = useDocumentContext(fetchingVariablesSelector);

  const getCurrentDesign = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [ currentDesign, updateArtboard] = useArtboardContext(getCurrentDesign);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const [attachmentsUpdated, setAttachmentsUpdated] = useState(false);

  useEffect(() => {
    const updateAttachments = async () => {
      if (attachments?.length <= 0) {
        return;
      }
      let attachmentModified = false;
      try {
        const promises = attachments.map(attachment =>
          attachment.documentId
            ? getComplexDocument(attachment.documentId, 'published')
            : new Promise((resolve, reject) => reject(false)),
        );
        const allResults = await Promise.allSettled(promises);
        allResults.forEach((response, index) => {
          if (response?.value?.data?.flows) {
            const existingData = emailMethods.get()[attachments[index].attachmentId];
            emailMethods.set({
              [attachments[index].attachmentId]: {
                ...existingData,
                flows: response.value.data.flows,
                attachmentBusinessLogics: response.value.data.businessLogics
              },
            });
            attachmentModified = true;
          }
        });
      } catch (e) {
        console.error(
          'error happened when fetching published documents for populating email preview',
        );
      } finally {
        if (attachmentModified) {
          setAttachmentsUpdated(true);
        }
      }
    };
    updateAttachments();
  }, [attachments]);

  const collectVariablesFromBusinessLogics = (businessLogics, variablesList) => {
    businessLogics.forEach(logic => {
      if (logic.variables?.length > 0) {
        logic.variables.forEach(variable => {
          const exists = variablesList.some(item => item.id === variable.variableId);
          if (!exists) {
            const variableDetails = allVariables.find(gVar => gVar.id === variable.variableId);
            if (variableDetails) {
              variablesList.push(variableDetails);
            }
          }
        });
      }
    });
  };

  const customSelector = useCallback(
    store => {
      let variablesList = [];
      if (variablesLoading) {
        return EMPTY_LIST;
      }
      const allFlows = store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS];
      const allBlocks = allFlows.reduce((acc, flowUUID) => {
        const flow = store[flowUUID];
        const flowBlocks = Object.keys(flow)
          .filter(key => key.startsWith(`${flowUUID}-`))
          .reduce((blockAcc, pageKey) => {
            return [...blockAcc, ...flow[pageKey].reduce((list, b) => {
                if (store[b].blockType === 'parent') {
                  return [...list, ...store[b].origContents];
                }
                return [...list, b];
              }, [])];
          }, []);
        const staticBlocks = [];
        if (flow.header) {
          staticBlocks.push(flow.header);
        }
        if (flow.footer) {
          staticBlocks.push(flow.footer);
        }
        return [...acc, ...flowBlocks, ...staticBlocks];
      }, []);
      allBlocks.forEach(blockUUID => {
        const block = store[blockUUID];
        let d = block.variableContract?.forEach(blockVariable => {
          const variableIndex = variablesList.findIndex(item => {
            return (
              (item.placeHolderText &&
                blockVariable.name &&
                item.placeHolderText === blockVariable.name) ||
              (item.id && blockVariable.variableId && item.id === blockVariable.variableId)
            );
          });
          if (variableIndex === -1) {
            const variableDetails = allVariables.find(gVar => gVar.id === blockVariable.variableId);
            if (variableDetails) {
              variablesList.push(variableDetails);
            } else if (blockVariable.type) {
              const tempBlock = getStoreStaticTableBlock(block, store);
              // Probably a local variable
              const localVariable = getLocalVariable(blockVariable, tempBlock, allVariables);
              variablesList.push(localVariable);
            }
          } else { // Check and merge variable contract of object type
            console.log({ blockVariable, variableDetails });
            let variableDetails = variablesList[variableIndex];
            variableDetails = mergeLocalVariable(
              variableDetails,
              blockVariable,
              allVariables,
              block
            );
            variablesList.splice(variableIndex, 1, variableDetails);
          }
        });
      });
      if (businessLogics?.length > 0) {
        collectVariablesFromBusinessLogics(businessLogics, variablesList);
      }
      if (subject?.variableContract?.length > 0) {
        subject.variableContract.forEach(contractVariable => {
          const variableIndex = variablesList.findIndex(
            item => item.id === contractVariable.variableId,
          );
          if (variableIndex === -1) {
            const variableDetails = allVariables.find(gVar => gVar.id === contractVariable.variableId);
            if (variableDetails) {
              variablesList.push(variableDetails);
            }
          }
        });
      }
      const variablesWithoutAttachment = deepCopy(variablesList);
      let attachmentOverrides = [];
      if (attachments?.length > 0) {
        attachments.forEach(attachment => {
          const attachmentDetails = emailMethods.get()[attachment.attachmentId];
          if (attachmentDetails.datasetOverride?.length) {
            attachmentOverrides = [...attachmentOverrides, attachmentDetails.datasetOverride];
          }
          const flows = attachmentDetails?.flows;
          const businessLogics = attachmentDetails?.attachmentBusinessLogics;
          let allExistingBlocks = [];
          flows &&
            flows.forEach(flow => {
              allExistingBlocks = [...flow.blocks];
              if (flow?.header) {
                allExistingBlocks.push(flow.header);
              }
              if (flow?.footer) {
                allExistingBlocks.push(flow.footer);
              }
              allExistingBlocks.forEach(block => {
                let d = block.variableContract?.forEach(blockVariable => {
                  const variableIndex = variablesList.findIndex(item => {
                    return (
                      (item.placeHolderText &&
                        blockVariable.name &&
                        item.placeHolderText === blockVariable.name) ||
                      (item.id && blockVariable.variableId && item.id === blockVariable.variableId)
                    );
                  });
                  if (variableIndex === -1) {
                    const variableDetails = allVariables.find(
                      gVar => gVar.id === blockVariable.variableId,
                    );
                    if (variableDetails) {
                      variablesList.push(variableDetails);
                    } else if (blockVariable.type) {
                      const tempBlock = getStoreStaticTableBlock(block, store, true);
                      // Probably a local variable
                      const localVariable = getLocalVariable(blockVariable, tempBlock, allVariables);
                      variablesList.push(localVariable);
                    }
                  } else {
                    // Check and merge variable contract of object type
                    let variableDetails = variablesList[variableIndex];
                    variableDetails = mergeLocalVariable(
                      variableDetails,
                      blockVariable,
                      allVariables,
                      block
                    );
                    variablesList.splice(variableIndex, 1, variableDetails);
                  }
                });
              });
            });
            if (businessLogics?.length > 0) {
              collectVariablesFromBusinessLogics(businessLogics, variablesList);
            }
        });
      }
      return { variablesList, variablesWithoutAttachment, attachmentOverrides };
    },
    [allVariables, variablesLoading, businessLogics, subject, attachments, attachmentsUpdated],
  );
  const [{ variablesList, variablesWithoutAttachment, attachmentOverrides }] = useDocumentContext(customSelector, 'ComplexEmailVariableList');


  /*useEffect(() => {
    console.log("Change in document Variables :: ", variablesList);
    const localVariables = [];
    let hasChanges = false;
    let tableName = undefined;

    const calculateLocalVariables = (list, parentName = undefined) => {
      for (let i = 0; i < list?.length; i++) {
        let contract = list[i];
        if (parentName === undefined) {
          tableName = contract.placeHolderText;
        }
        if (contract.id !== undefined) {
          continue;
        } else {
          if (contract.type === 'tableObject') {
            calculateLocalVariables(contract.variables, contract.name);
          } else if (contract.type === 'object') {
            let tableContract = contract.variables.find(c => c.type === 'tableObject');
            if (tableContract) {
              tableName = tableContract.placeHolderText;
              calculateLocalVariables(tableContract.variables, tableContract.placeHolderText);
            }
          } else if (PRIMITIVE_VARIABLE_TYPES.includes(contract.type) && contract.id === undefined) {
              let variableCategoryName = parentName.replaceAll(`${tableName}.`, '').split('.')[0];
              let placeHolderText = contract.name.replace(`${tableName}.`, '');
              if (localVariables.find(lv => lv.placeHolderText === placeHolderText) === undefined) {
                hasChanges = true;
                localVariables.push({
                  name: contract.placeHolderText,
                  placeHolderText,
                  type: contract.type,
                  variableCategoryId: 0,
                  variableCategoryName
                });
              }
          }
        }
      }
    }

    calculateLocalVariables(variablesList);
    if (hasChanges) {
      method.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.LOCAL_VARIABLES]: localVariables
      }, [], true);
      console.log("local variables set :: ", localVariables, method.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.LOCAL_VARIABLES])
    }
  }, [variablesList]);*/

  useEffect(() => {
    if(!visible){
      updateArtboard({ [CONTEXT_KEYS.IS_PREVIEW]: false });
    }
  }, [visible])

  return (
    <>
      {visible && (
        <ComplexPreviewTabsContainer>
          <Tabs tabBarStyle={{ color: 'white' }}>
            <TabPane tab={
              checkUserAccessRoles(userDetails, ['DESIGNER', 'PUBLISHER']) && currentDesign === 'document'
                ? t(COMPLEX_VARIABLES_SIDEBAR.PREVIEW_AND_SEND_BUTTON.key, COMPLEX_VARIABLES_SIDEBAR.PREVIEW_AND_SEND_BUTTON.value)
                : t(COMPLEX_VARIABLES_SIDEBAR.PREVIEW_LABEL.key, COMPLEX_VARIABLES_SIDEBAR.PREVIEW_LABEL.value)}
              key="1">
              <ComplexEmailVariableList
                documentVariables={variablesList}
                globalVariables={allVariables}
                businessLogics={businessLogics}
                subject={subject}
                fromAddressId={fromAddressId}
                attachments={attachments}
                setIsHtmlExpanded={setIsHtmlExpanded}
                isHtmlExpanded={isHtmlExpanded}
              />
            </TabPane>
            {currentDesign === TEMPLATE_TYPE.DOCUMENT ? (
              <TabPane tab={t(COMPLEX_VARIABLES_SIDEBAR.OVERRIDE_VARIABLES_LABEL.key, COMPLEX_VARIABLES_SIDEBAR.OVERRIDE_VARIABLES_LABEL.value)} key="2">
                <ComplexVariableOverrideList variablesList={variablesWithoutAttachment} editable={editable} attachmentOverrides={attachmentOverrides} isEmailTemplate={true}/>
              </TabPane>
            ) : null}
          </Tabs>
        </ComplexPreviewTabsContainer>
      )}
      {document.getElementById('document-variable-counter') &&
        ReactDOM.createPortal(
          <span className="docVarCount">{variablesList.length}</span>,
          document.getElementById('document-variable-counter'),
        )}
    </>
  );
};

export default ComplexEmailVariablesTab;